import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFESSIONS = [
  'All Professions',
  'Ex Army Officer', 'Farmer', 'Housewife', 'Retired Person', 'Driver',
  'Banker', 'Police', 'Teacher', 'Shopkeeper', 'Government Servant',
  'Lawyer', 'CA', 'Doctor', 'IT Professional', 'Architect',
  'Engineer', 'Investor', 'Businessman', 'Cricketer', 'Actor',
  'Politician', 'Judge', 'Commissioner', 'Deputy Collector'
];

const LAND_SIZES = [
  'All Sizes', '2 R', '5 R', '10 R', '20 R', '1 Acre', '2 Acre', '5 Acre', '10 Acre'
];

const TestimonialCard = ({ testimonial }) => (
  <div
    data-testid={`testimonial-card-${testimonial.id}`}
    className="bg-white border border-stone-200 hover:border-emerald-900 transition-colors duration-300 overflow-hidden"
  >
    {testimonial.video_url ? (
      <div className="aspect-video bg-stone-900">
        {testimonial.video_url.includes('youtube.com') || testimonial.video_url.includes('vimeo.com') ? (
          <iframe
            src={testimonial.video_url}
            title={`${testimonial.client_name} testimonial`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <video
            controls
            className="w-full h-full object-cover"
            preload="metadata"
          >
            <source src={testimonial.video_url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
    ) : (
      <div className="p-6 bg-stone-50">
        <div className="flex items-center gap-1 mb-4">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-5 h-5 ${i < testimonial.rating ? 'fill-amber-600 text-amber-600' : 'text-stone-300'}`}
            />
          ))}
        </div>
        <p className="text-stone-700 text-lg leading-relaxed italic">"{testimonial.message}"</p>
      </div>
    )}
    <div className="p-6">
      <div className="flex items-center gap-4">
        {testimonial.image_url && !testimonial.video_url && (
          <img
            src={testimonial.image_url}
            alt={testimonial.client_name}
            className="w-16 h-16 rounded-full object-cover border-2 border-stone-200"
          />
        )}
        <div>
          <p className="font-bold text-stone-900 text-lg">{testimonial.client_name}</p>
          {testimonial.profession && (
            <p className="text-emerald-900 text-sm font-medium uppercase tracking-widest mt-1">
              {testimonial.profession}
            </p>
          )}
          {testimonial.guntha_size && (
            <p className="text-amber-600 text-sm font-bold uppercase tracking-widest mt-1">
              {testimonial.guntha_size}
            </p>
          )}
          {testimonial.company && (
            <p className="text-stone-500 text-sm uppercase tracking-widest mt-1">
              {testimonial.company}
            </p>
          )}
        </div>
      </div>
    </div>
  </div>
);

const TestimonialSection = ({ type, testimonials, professionFilter = true, gunthaFilter = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(professionFilter ? 'All Professions' : 'All Sizes');
  const [displayedTestimonials, setDisplayedTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    filterAndRotate();
    const interval = setInterval(() => {
      rotateTestimonials();
    }, 10000); // Rotate every 10 seconds
    return () => clearInterval(interval);
  }, [testimonials, searchQuery, selectedFilter]);

  const filterAndRotate = () => {
    let filtered = testimonials;

    // Filter by profession or guntha
    if (professionFilter && selectedFilter !== 'All Professions') {
      filtered = filtered.filter(t => t.profession && t.profession.toLowerCase() === selectedFilter.toLowerCase());
    }
    if (gunthaFilter && selectedFilter !== 'All Sizes') {
      filtered = filtered.filter(t => t.guntha_size && t.guntha_size === selectedFilter);
    }

    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(t =>
        t.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.profession && t.profession.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.company && t.company.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.guntha_size && t.guntha_size.toLowerCase().includes(searchQuery.toLowerCase())) ||
        t.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setDisplayedTestimonials(filtered.slice(0, 4));
    setCurrentIndex(0);
  };

  const rotateTestimonials = () => {
    let filtered = testimonials;
    
    if (professionFilter && selectedFilter !== 'All Professions') {
      filtered = filtered.filter(t => t.profession && t.profession.toLowerCase() === selectedFilter.toLowerCase());
    }
    if (gunthaFilter && selectedFilter !== 'All Sizes') {
      filtered = filtered.filter(t => t.guntha_size && t.guntha_size === selectedFilter);
    }
    if (searchQuery) {
      filtered = filtered.filter(t =>
        t.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.profession && t.profession.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.company && t.company.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.guntha_size && t.guntha_size.toLowerCase().includes(searchQuery.toLowerCase())) ||
        t.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filtered.length > 4) {
      const nextIndex = (currentIndex + 4) % filtered.length;
      setCurrentIndex(nextIndex);
      const nextBatch = [];
      for (let i = 0; i < 4; i++) {
        nextBatch.push(filtered[(nextIndex + i) % filtered.length]);
      }
      setDisplayedTestimonials(nextBatch);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            type="text"
            placeholder="Search testimonials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
          />
        </div>

        <Select value={selectedFilter} onValueChange={setSelectedFilter}>
          <SelectTrigger className="h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900">
            <SelectValue placeholder={professionFilter ? "Filter by Profession" : "Filter by Guntha Size"} />
          </SelectTrigger>
          <SelectContent>
            {(professionFilter ? PROFESSIONS : LAND_SIZES).map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <p className="text-stone-600 text-sm">
        Showing {displayedTestimonials.length} of {testimonials.length} testimonials
        {selectedFilter !== 'All Professions' && selectedFilter !== 'All Sizes' && ` (${selectedFilter})`}
        {testimonials.length > 4 && <span className="ml-2 text-emerald-900 font-medium">• Rotating automatically</span>}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {displayedTestimonials.map((testimonial) => (
          <TestimonialCard key={testimonial.id} testimonial={testimonial} />
        ))}
      </div>

      {displayedTestimonials.length === 0 && (
        <div className="text-center py-20 bg-white border border-stone-200">
          <p className="text-stone-500 text-lg">No testimonials found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

const Testimonials = () => {
  const [allTestimonials, setAllTestimonials] = useState([]);
  const [channelPartnerTestimonials, setChannelPartnerTestimonials] = useState([]);
  const [customerTestimonials, setCustomerTestimonials] = useState([]);
  const [gunthaTestimonials, setGunthaTestimonials] = useState([]);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      const data = response.data;
      setAllTestimonials(data);
      
      setChannelPartnerTestimonials(data.filter(t => t.testimonial_type === 'Channel Partner'));
      setCustomerTestimonials(data.filter(t => t.testimonial_type === 'Customer'));
      setGunthaTestimonials(data.filter(t => t.testimonial_type === 'Guntha'));
    } catch (error) {
    }
  };

  return (
    <div data-testid="testimonials-page">
      <section
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/9b3f41d0-ec42-4909-9b7d-d89c1a664dc2/images/7773eeeb2d61ab985977a29dd72b48e15f95c355849c0280acda8a7aba49519a.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-stone-900/65" />
        <div className="relative z-10 text-center px-6 text-white">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold tracking-tight mb-4">
            Client Testimonials
          </h1>
          <p className="text-xl text-stone-200">Hear What Our Clients Say About Us</p>
          <div className="mt-10 animate-bounce">
            <svg className="w-8 h-8 mx-auto text-stone-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 lg:px-12 max-w-7xl mx-auto">
        <Tabs defaultValue="channel" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-auto mb-12">
            <TabsTrigger 
              value="channel" 
              className="text-sm sm:text-base py-4 data-[state=active]:bg-emerald-900 data-[state=active]:text-white"
            >
              Channel Partner ({channelPartnerTestimonials.length})
            </TabsTrigger>
            <TabsTrigger 
              value="customer" 
              className="text-sm sm:text-base py-4 data-[state=active]:bg-emerald-900 data-[state=active]:text-white"
            >
              Customer ({customerTestimonials.length})
            </TabsTrigger>
            <TabsTrigger 
              value="guntha" 
              className="text-sm sm:text-base py-4 data-[state=active]:bg-emerald-900 data-[state=active]:text-white"
            >
              Land Size ({gunthaTestimonials.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="channel">
            <div className="mb-6">
              <h2 className="text-3xl font-playfair font-bold text-stone-900 mb-2">Channel Partner Testimonials</h2>
              <p className="text-stone-600">Testimonials from our valued channel partners</p>
            </div>
            <TestimonialSection 
              type="Channel Partner" 
              testimonials={channelPartnerTestimonials}
              professionFilter={true}
            />
          </TabsContent>

          <TabsContent value="customer">
            <div className="mb-6">
              <h2 className="text-3xl font-playfair font-bold text-stone-900 mb-2">Customer Testimonials</h2>
              <p className="text-stone-600">Real experiences from our satisfied customers</p>
            </div>
            <TestimonialSection 
              type="Customer" 
              testimonials={customerTestimonials}
              professionFilter={true}
            />
          </TabsContent>

          <TabsContent value="guntha">
            <div className="mb-6">
              <h2 className="text-3xl font-playfair font-bold text-stone-900 mb-2">Land Size Wise Testimonials</h2>
              <p className="text-stone-600">Testimonials based on land sizes purchased</p>
            </div>
            <TestimonialSection 
              type="Guntha" 
              testimonials={gunthaTestimonials}
              professionFilter={false}
              gunthaFilter={true}
            />
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
};

export default Testimonials;
